<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div class="text-center mb-2.5">
                <h1 class="text-4xl md:text-6xl font-marketing font-bold text-primary mb-1.5">
                    Our Partners
                </h1>
                <div v-if="pageDescription"
                    class="text-xl text-gray-700 max-w-4xl mx-auto font-sans leading-relaxed content-page-description"
                    v-html="pageDescription">
                </div>
                <p v-else class="text-xl text-gray-700 max-w-4xl mx-auto font-sans leading-relaxed">
                    Building meaningful partnerships to amplify diverse voices and expand our reach.
                </p>
            </div>

            <div v-if="loadingPartners" class="text-center py-12">
                <p class="text-gray-600 font-sans">Loading partners...</p>
            </div>

            <div v-else-if="partners.length === 0" class="max-w-4xl mx-auto">
                <div class="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center">
                    <div class="mb-8">
                        <svg class="w-24 h-24 mx-auto text-gray-300 mb-6" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h2 class="text-3xl md:text-4xl font-marketing font-bold text-primary mb-8">
                        Currently No Partners
                    </h2>
                    <p class="text-lg text-gray-700 font-sans leading-relaxed mb-8 max-w-2xl mx-auto">
                        We are actively seeking partnerships with organizations, institutions, and businesses that share
                        our mission of amplifying diverse voices and making literature accessible to all. If you are
                        interested in partnering with <i>Barry & Bonna Publishing</i>, we would love to hear from you.
                    </p>
                    <button @click="() => window.location.href = '/contact'"
                        class="bg-accent text-white px-8 py-3 rounded-md hover:bg-green-700 transition-colors font-marketing font-medium">
                        Contact Us for Partnerships
                    </button>
                </div>
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <div v-for="partner in partners" :key="partner.id"
                    class="bg-white flex flex-col transition-all shadow-md hover:shadow-lg max-w-sm min-h-[400px] mx-auto rounded-lg">
                    <!-- Image section -->
                    <div class="bg-gradient-to-b from-gray-100 to-gray-200 flex items-center justify-center h-40 sm:h-48 px-4 sm:px-8 rounded-t-lg overflow-hidden flex-shrink-0">
                        <div v-if="partner.image_url" class="w-full h-full flex items-center justify-center">
                            <img :src="partner.image_url" :alt="partner.name"
                                class="max-w-full max-h-full w-auto h-auto object-contain" />
                        </div>
                        <div v-else class="text-gray-400 font-sans text-sm">
                            No image
                        </div>
                    </div>
                    <!-- Body section -->
                    <div class="bg-white px-4 sm:px-6 pt-4 sm:pt-6 flex flex-col flex-grow"
                        :class="{ 'pb-4': !shouldShowReadMore(partner), 'sm:pb-6': !shouldShowReadMore(partner) }">
                        <h3 class="text-lg sm:text-xl font-marketing font-bold text-black mb-3 line-clamp-2">
                            {{ partner.name }}
                        </h3>
                        <div class="flex-grow">
                            <div v-if="partner.description"
                                class="text-sm sm:text-base text-black font-sans leading-relaxed mb-3 content-page-section">
                                <div class="partner-description line-clamp-4" v-html="partner.description"></div>
                            </div>
                            <div v-if="partner.capacity"
                                class="text-sm sm:text-base text-black font-sans leading-relaxed italic mb-3 content-page-section">
                                <div class="partner-capacity line-clamp-2" v-html="partner.capacity"></div>
                            </div>
                            <div v-if="partner.links && partner.links.length > 0" class="mb-2">
                                <div v-for="link in partner.links" :key="link.url" class="mb-1">
                                    <a :href="link.url" target="_blank" rel="noopener noreferrer"
                                        class="text-primary hover:text-primary-dark font-sans text-xs sm:text-sm underline">
                                        {{ link.label }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Footer section -->
                    <div v-if="shouldShowReadMore(partner)" class="px-4 sm:px-6 pb-4 sm:pb-6 pt-2 flex-shrink-0">
                        <button @click="openModal(partner)"
                            class="text-primary hover:text-primary-dark font-marketing font-semibold text-sm flex items-center group">
                            <span>Read more</span>
                            <svg class="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="selectedPartner"
            class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            @click.self="closeModal">
            <div
                class="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col relative">
                <button @click="closeModal"
                    class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                    aria-label="Close">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <div
                    class="bg-gradient-to-b from-gray-100 to-gray-200 flex items-center justify-center h-32 sm:h-48 px-4 sm:px-8 flex-shrink-0">
                    <div v-if="selectedPartner.image_url" class="w-full h-full flex items-center justify-center">
                        <img :src="selectedPartner.image_url" :alt="selectedPartner.name"
                            class="max-w-full max-h-full w-auto h-auto object-contain" />
                    </div>
                    <div v-else class="text-gray-400 font-sans text-sm">
                        No image
                    </div>
                </div>
                <div class="bg-white px-4 sm:px-6 md:px-8 py-4 sm:py-6 flex flex-col flex-grow overflow-y-auto">
                    <h2 class="text-xl sm:text-2xl font-marketing font-bold text-black mb-4">
                        {{ selectedPartner.name }}
                    </h2>
                    <div v-if="selectedPartner.description" class="mb-4">
                        <div class="text-sm sm:text-base text-black font-sans leading-relaxed content-page-section"
                            v-html="selectedPartner.description"></div>
                    </div>
                    <div v-if="selectedPartner.capacity" class="mb-4">
                        <div class="text-sm sm:text-base text-black font-sans leading-relaxed italic content-page-section"
                            v-html="selectedPartner.capacity"></div>
                    </div>
                    <div v-if="selectedPartner.links && selectedPartner.links.length > 0" class="mb-4">
                        <h3 class="text-lg font-marketing font-semibold text-black mb-2">Links</h3>
                        <div class="space-y-2">
                            <div v-for="link in selectedPartner.links" :key="link.url">
                                <a :href="link.url" target="_blank" rel="noopener noreferrer"
                                    class="text-primary hover:text-primary-dark font-sans text-base underline">
                                    {{ link.label }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { publicService } from '../services/public'
import { stripHtml } from '../utils/htmlTruncation'

const partners = ref([])
const loadingPartners = ref(false)
const selectedPartner = ref(null)
const pageDescription = ref('')

const loadPartners = async () => {
    loadingPartners.value = true
    try {
        const response = await publicService.getPartners()
        if (response && response.data && Array.isArray(response.data)) {
            partners.value = response.data
        } else if (Array.isArray(response)) {
            partners.value = response
        } else {
            partners.value = []
        }
    } catch (error) {
        console.error('Failed to load partners:', error)
        partners.value = []
    } finally {
        loadingPartners.value = false
    }
}

const shouldShowReadMore = (partner) => {
    // Show read more if partner has substantial content
    const hasDescription = partner.description && stripHtml(partner.description).length > 150
    const hasCapacity = partner.capacity && stripHtml(partner.capacity).length > 80
    const hasLinks = partner.links && partner.links.length > 2
    return hasDescription || hasCapacity || hasLinks
}

const openModal = (partner) => {
    selectedPartner.value = partner
}

const closeModal = () => {
    selectedPartner.value = null
}

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage('Partners')
        if (page && page.description) {
            pageDescription.value = page.description
        }
    } catch (error) {
        console.error('Failed to load page description:', error)
    }
}

onMounted(() => {
    loadPartners()
    loadPageDescription()
})
</script>

<style scoped>
/* Comprehensive Quill Editor Content Rendering */
:deep(h1) {
    font-size: 2.5rem;
    font-weight: bold;
    color: #111111;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
}

:deep(h2) {
    font-size: 2rem;
    font-weight: bold;
    color: #111111;
    margin-top: 1.25rem;
    margin-bottom: 0.75rem;
}

:deep(h3) {
    font-size: 1.5rem;
    font-weight: bold;
    color: #111111;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
}

/* Font Sizes */
:deep(.ql-size-small) {
    font-size: 0.75rem;
}

:deep(.ql-size-large) {
    font-size: 1.5rem;
}

:deep(.ql-size-huge) {
    font-size: 2.5rem;
}

/* Blockquotes */
:deep(blockquote) {
    border-left: 4px solid #ccc;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #666;
}

/* Code Blocks */
:deep(pre) {
    background-color: #f4f4f4;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 1rem 0;
}

:deep(code) {
    background-color: #f4f4f4;
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
}

:deep(pre code) {
    background-color: transparent;
    padding: 0;
}

/* Images */
:deep(img) {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
}

/* Videos and Iframes */
:deep(iframe),
:deep(video) {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
}

/* Text Alignment */
:deep(.ql-align-center) {
    text-align: center;
}

:deep(.ql-align-right) {
    text-align: right;
}

:deep(.ql-align-justify) {
    text-align: justify;
}

:deep([style*='text-align: center']) {
    text-align: center;
}

:deep([style*='text-align: right']) {
    text-align: right;
}

:deep([style*='text-align: justify']) {
    text-align: justify;
}

/* Font Families */
:deep(.ql-font-serif) {
    font-family: Georgia, 'Times New Roman', serif;
}

:deep(.ql-font-monospace) {
    font-family: 'Courier New', Courier, monospace;
}

/* Lists */
:deep(ul),
:deep(ol) {
    margin: 1rem 0;
    padding-left: 1.5rem;
}

:deep(li) {
    margin-bottom: 0.5rem;
}

/* Links */
:deep(a) {
    color: #253439;
    text-decoration: underline;
    transition: color 0.2s;
}

:deep(a:hover) {
    color: #1a2528;
}

/* Spacing */
:deep(p) {
    margin-bottom: 1rem;
}

/* Partner Card Specific Styling */
.partner-description,
.partner-capacity {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.partner-description.line-clamp-4 {
    -webkit-line-clamp: 4;
}

.partner-capacity.line-clamp-2 {
    -webkit-line-clamp: 2;
}

/* Responsive line-clamp adjustments */
@media (max-width: 640px) {
    .partner-description.line-clamp-4 {
        -webkit-line-clamp: 3;
    }
    
    .partner-capacity.line-clamp-2 {
        -webkit-line-clamp: 1;
    }
}

/* Ensure proper text overflow handling */
.line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

/* Animation for read more button */
.group:hover .group-hover\:translate-x-1 {
    transform: translateX(0.25rem);
}
</style>
